import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  PhotoIcon,
  EyeIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import { API_BASE } from '../lib/config';

// API base is provided by shared config (CRA/Next compatible)
const API = `${API_BASE}`;

const QualificationApproval = () => {
  const [qualifications, setQualifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchQualifications();
  }, [filterStatus]);

  const fetchQualifications = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const url = filterStatus === 'all' 
        ? `${API}/admin/qualifications`
        : `${API}/admin/qualifications?status=${filterStatus}`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setQualifications(data);
      } else {
        toast.error('資格情報の取得に失敗しました');
      }
    } catch (error) {
      console.error('Error fetching qualifications:', error);
      toast.error('データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (qualificationId, status, rejectionReason = '') => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API}/admin/qualifications/${qualificationId}/review`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          status,
          rejection_reason: rejectionReason
        })
      });

      if (response.ok) {
        toast.success(status === 'approved' ? '資格を承認しました' : '資格を却下しました');
        fetchQualifications();
      } else {
        toast.error('処理に失敗しました');
      }
    } catch (error) {
      console.error('Error reviewing qualification:', error);
      toast.error('処理に失敗しました');
    }
  };

  const handleApprove = (qualificationId) => {
    if (window.confirm('この資格を承認しますか？')) {
      handleReview(qualificationId, 'approved');
    }
  };

  const handleReject = (qualificationId) => {
    const reason = window.prompt('却下理由を入力してください（任意）:', '');
    if (reason !== null) { // Cancel was not clicked
      handleReview(qualificationId, 'rejected', reason);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return (
          <div className="flex items-center text-green-600">
            <CheckCircleIcon className="h-4 w-4 mr-1" />
            <span className="text-sm font-medium">承認済み</span>
          </div>
        );
      case 'rejected':
        return (
          <div className="flex items-center text-red-600">
            <XCircleIcon className="h-4 w-4 mr-1" />
            <span className="text-sm font-medium">却下</span>
          </div>
        );
      default:
        return (
          <div className="flex items-center text-yellow-600">
            <ClockIcon className="h-4 w-4 mr-1" />
            <span className="text-sm font-medium">審査待ち</span>
          </div>
        );
    }
  };

  const filteredQualifications = qualifications.filter(q => {
    if (filterStatus === 'all') return true;
    return q.status === filterStatus;
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">保有資格承認管理</h2>
        
        {/* フィルター */}
        <div className="flex space-x-2">
          {[
            { key: 'all', label: 'すべて', count: qualifications.length },
            { key: 'pending', label: '審査待ち', count: qualifications.filter(q => q.status === 'pending').length },
            { key: 'approved', label: '承認済み', count: qualifications.filter(q => q.status === 'approved').length },
            { key: 'rejected', label: '却下', count: qualifications.filter(q => q.status === 'rejected').length }
          ].map(filter => (
            <button
              key={filter.key}
              onClick={() => setFilterStatus(filter.key)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterStatus === filter.key
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {filter.label} ({filter.count})
            </button>
          ))}
        </div>
      </div>

      {filteredQualifications.length === 0 ? (
        <div className="text-center py-12">
          <UserIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">
            {filterStatus === 'all' ? '資格登録がありません' : `${filterStatus}の資格がありません`}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="divide-y divide-gray-200">
            {filteredQualifications.map((qualification) => (
              <div key={qualification.id} className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <UserIcon className="h-5 w-5 text-gray-400 mr-2" />
                      <span className="font-medium text-gray-900">{qualification.user_name}</span>
                      <span className="text-sm text-gray-500 ml-2">({qualification.user_email})</span>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {qualification.qualification_name}
                    </h3>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      {qualification.issuing_organization && (
                        <div>
                          <span className="text-gray-500">発行機関:</span>
                          <span className="ml-2 text-gray-900">{qualification.issuing_organization}</span>
                        </div>
                      )}
                      {qualification.acquisition_date && (
                        <div>
                          <span className="text-gray-500">取得年月:</span>
                          <span className="ml-2 text-gray-900">{qualification.acquisition_date}</span>
                        </div>
                      )}
                      <div>
                        <span className="text-gray-500">申請日:</span>
                        <span className="ml-2 text-gray-900">
                          {new Date(qualification.created_at).toLocaleDateString('ja-JP')}
                        </span>
                      </div>
                      {qualification.reviewed_at && (
                        <div>
                          <span className="text-gray-500">審査日:</span>
                          <span className="ml-2 text-gray-900">
                            {new Date(qualification.reviewed_at).toLocaleDateString('ja-JP')}
                          </span>
                        </div>
                      )}
                    </div>

                    {qualification.rejection_reason && (
                      <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <span className="text-sm text-red-800">
                          <strong>却下理由:</strong> {qualification.rejection_reason}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <div className="ml-6 flex flex-col items-end space-y-3">
                    {getStatusBadge(qualification.status)}
                    
                    {/* 証明書画像 */}
                    {qualification.certificate_image && (
                      <div className="relative">
                        <img 
                          src={qualification.certificate_image} 
                          alt="証明書" 
                          className="w-24 h-18 object-cover rounded border cursor-pointer hover:opacity-80"
                          onClick={() => setSelectedImage(qualification.certificate_image)}
                        />
                        <button
                          onClick={() => setSelectedImage(qualification.certificate_image)}
                          className="absolute top-1 right-1 bg-black bg-opacity-50 text-white p-1 rounded"
                        >
                          <EyeIcon className="h-3 w-3" />
                        </button>
                      </div>
                    )}

                    {/* アクションボタン */}
                    {qualification.status === 'pending' && (
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleApprove(qualification.id)}
                          className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors text-sm"
                        >
                          承認
                        </button>
                        <button
                          onClick={() => handleReject(qualification.id)}
                          className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition-colors text-sm"
                        >
                          却下
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 画像プレビューモーダル */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="relative max-w-4xl max-h-4xl p-4">
            <img 
              src={selectedImage} 
              alt="証明書プレビュー" 
              className="max-w-full max-h-full object-contain"
            />
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-2 right-2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75"
            >
              <XCircleIcon className="h-6 w-6" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default QualificationApproval;